import {
  Checkbox,
  HStack,
  Icon,
  StackProps,
  Table,
  TableCellProps,
  TableColumnHeaderProps,
  TableRowProps,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
} from '@chakra-ui/react';
import { TbChevronDown, TbChevronUp } from 'react-icons/tb';
import React, { Fragment, ReactNode, useMemo, useRef } from 'react';

import { useShiftKey } from '../support/useShiftKey';

import { ListEmptyState } from './ListEmptyState';

// I'm using true/false here instead of an enum or union to make it easier for
// the parent component to keep this in state.
export const Sort = {
  Asc: false,
  Desc: true,
} as const;

export type SortBy = [column: number, direction: boolean];

export type Column<T> = {
  title: string;
  render: (item: T) => ReactNode;
  sortable?: { sortFn: (a: T, b: T) => number };
  thAlign?: StackProps['justify'];
  thProps?: TableColumnHeaderProps;
  tdProps?: TableCellProps;
  onClick?: (
    event: React.MouseEvent<HTMLTableDataCellElement, MouseEvent>,
  ) => void;
};

type Props<T extends { id: string }> = {
  data: Array<T>;
  columns: Array<Column<T>>;
  selected?: Set<string>;
  onSelectionChange?: (selected: Set<string>) => void;
  onRowClick?: (item: T) => void;
  renderRow?: (
    item: T,
    render: (props?: TableRowProps) => ReactNode,
  ) => ReactNode;
  isRowClickable?: (item: T) => boolean;
  sortBy?: SortBy;
  onSortChange?: (sortBy: SortBy) => void;
  renderEmptyState?: () => ReactNode;
};

export { Props as TableViewProps };

export function TableView<T extends { id: string }>(props: Props<T>) {
  const {
    columns,
    onSelectionChange,
    onRowClick,
    isRowClickable,
    renderRow,
    sortBy,
    onSortChange,
    renderEmptyState,
  } = props;
  const selectable = onSelectionChange != null;
  const selected = props.selected ?? new Set();
  const columnCount = selectable ? columns.length + 1 : columns.length;

  // If there's no row click handler, then clicking a row should select that row, if rows are selectable
  const rowClickHandler =
    onRowClick ?? (selectable ? (item) => onSelect(item.id) : null);

  const [sortCol, sortDir] = sortBy ?? [null, false];
  const data = useMemo(() => {
    const items = props.data;
    if (sortCol == null) {
      return items;
    }
    const sortFn = columns[sortCol]?.sortable?.sortFn;
    if (!sortFn) {
      return items;
    }
    // TODO: Better handle sort order
    const sorted = items.slice().sort(sortFn);
    return sortDir === Sort.Desc ? sorted.reverse() : sorted;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.data, sortCol, sortDir]);

  const onSortClick = (index: number) => {
    const sortable = columns[index]?.sortable;
    if (sortable) {
      const prevSortDir = index === sortCol ? sortDir : Sort.Desc;
      const nextSortDir = prevSortDir === Sort.Asc ? Sort.Desc : Sort.Asc;
      onSortChange?.([index, nextSortDir]);
    }
  };

  const isShiftKeyDown = useShiftKey();

  const prevSelectionChange = useRef<string | null>(null);
  const onSelect = (id: string) => {
    const sel = new Set(selected);
    if (isShiftKeyDown.current) {
      const prevId = prevSelectionChange.current;
      const prevIndex = data.findIndex((item) => item.id === prevId);
      const nextIndex = data.findIndex((item) => item.id === id);
      const startIndex = Math.min(prevIndex, nextIndex);
      const endIndex = Math.max(prevIndex, nextIndex);
      data.forEach((item, index) => {
        if (index >= startIndex && index <= endIndex) {
          sel.add(item.id);
        }
      });
    } else {
      sel.has(id) ? sel.delete(id) : sel.add(id);
    }
    onSelectionChange?.(sel);
    prevSelectionChange.current = id;
  };

  const isAnySelected = selected.size !== 0;
  const isAllSelected = selected.size === data.length;

  return (
    <Table width="full">
      <Thead
        position="sticky"
        zIndex={1}
        top={0}
        bg="white"
        boxShadow="0 1px var(--chakra-colors-gray-100)"
      >
        <Tr>
          {selectable ? (
            <Th>
              <Checkbox
                isChecked={isAnySelected}
                isIndeterminate={isAnySelected && !isAllSelected}
                onChange={() => {
                  const sel = new Set<string>();
                  if (!isAllSelected) {
                    data.forEach((item) => sel.add(item.id));
                  }
                  onSelectionChange?.(sel);
                }}
              />
            </Th>
          ) : null}
          {columns.map((column, i) => {
            const { title, thAlign, thProps } = column;
            const isSortable = column.sortable != null;
            const sortIcon = i === sortCol ? sortDir : null;
            return (
              <Th
                key={i}
                whiteSpace="nowrap"
                cursor={isSortable ? 'pointer' : undefined}
                onClick={() => onSortClick(i)}
                {...thProps}
              >
                <VStack align="stretch">
                  <HStack justify={thAlign}>
                    <Text>{title}</Text>
                    {sortIcon === Sort.Asc ? (
                      <Icon as={TbChevronDown} />
                    ) : sortIcon === Sort.Desc ? (
                      <Icon as={TbChevronUp} />
                    ) : null}
                  </HStack>
                </VStack>
              </Th>
            );
          })}
        </Tr>
      </Thead>
      <Tbody>
        {data.length === 0 ? (
          <Tr>
            <Td colSpan={columnCount}>
              {renderEmptyState ? renderEmptyState() : <ListEmptyState />}
            </Td>
          </Tr>
        ) : null}
        {data.map((item) => {
          const isClickable = isRowClickable ? isRowClickable(item) : true;
          const onRowClick = isClickable ? rowClickHandler : undefined;
          const render = (props?: TableRowProps) => (
            <Tr
              _hover={{ backgroundColor: 'gray.50' }}
              cursor={onRowClick ? 'pointer' : undefined}
              onClick={() => onRowClick?.(item)}
              {...props}
            >
              {selectable ? (
                <Td onClick={(event) => event.stopPropagation()}>
                  <Checkbox
                    isChecked={selected.has(item.id)}
                    onChange={() => onSelect(item.id)}
                  />
                </Td>
              ) : null}
              {columns.map(({ render, onClick, tdProps }, i) => (
                <Td key={i} onClick={onClick} {...tdProps}>
                  {render(item)}
                </Td>
              ))}
            </Tr>
          );
          return (
            <Fragment key={item.id}>
              {renderRow ? renderRow(item, render) : render()}
            </Fragment>
          );
        })}
      </Tbody>
    </Table>
  );
}
